import { usePlayerStore } from '../store/playerStore';
import { useRadioStore } from '../store/radioStore';
import { Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, ListMusic, Radio, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import LikeButton from './LikeButton';

export default function PlayerBar() {
  // Track player state
  const {
    currentTrack: trackCurrentTrack,
    isPlaying: trackIsPlaying,
    volume: trackVolume,
    currentTime,
    duration: trackDuration,
    queue,
    history,
    pause: trackPause,
    resume: trackResume,
    next,
    previous,
    seek,
    setVolume: trackSetVolume,
  } = usePlayerStore();

  // Radio state
  const {
    currentTrack: radioCurrentTrack,
    isPlaying: radioIsPlaying,
    volume: radioVolume,
    elapsed: radioElapsed,
    duration: radioDuration,
    profileSlug,
    mood,
    pause: radioPause,
    resume: radioResume,
    stopRadio,
    setVolume: radioSetVolume,
  } = useRadioStore();

  const [showQueue, setShowQueue] = useState(false);

  // Determine which mode is active
  const isRadioMode = radioCurrentTrack !== null;

  // Unified state based on mode
  const currentTrack = isRadioMode ? radioCurrentTrack : trackCurrentTrack;
  const isPlaying = isRadioMode ? radioIsPlaying : trackIsPlaying;
  const volume = isRadioMode ? radioVolume : trackVolume;
  const duration = isRadioMode ? radioDuration : trackDuration;
  const currentTimeValue = isRadioMode ? radioElapsed : currentTime;

  const pause = isRadioMode ? radioPause : trackPause;
  const resume = isRadioMode ? radioResume : trackResume;
  const setVolume = isRadioMode ? radioSetVolume : trackSetVolume;

  // Visibility is now decided by the parent: Layout renders PlayerBar
  // for non-radio-first routes, and StickyRadioBar wraps PlayerBar
  // for radio-first routes (toggled by scroll position via
  // IntersectionObserver on the hero). PlayerBar itself only short-
  // circuits when there's nothing playing.
  if (!currentTrack) {
    return null;
  }

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Don't allow seeking in radio mode
    if (isRadioMode) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    seek(newTime);
  };

  const toggleMute = () => {
    if (volume > 0) {
      setVolume(0);
    } else {
      setVolume(0.8);
    }
  };

  const progress = duration > 0 ? (currentTimeValue / duration) * 100 : 0;

  return (
    <>
      {/* Desktop Player Bar */}
      <div className="hidden md:block fixed bottom-0 left-0 right-0 backdrop-blur-2xl bg-dark-950/95 text-white border-t border-white/10 z-50 shadow-2xl">
        <div className="max-w-screen-2xl mx-auto px-6 py-4">
          <div className="flex items-center gap-8">
            {/* Track Info */}
            <div className="flex items-center gap-4 min-w-0 flex-1">
              {/* Radio indicator */}
              {isRadioMode && (
                <Link
                  to={`/radio/${profileSlug}/${mood}`}
                  className="flex items-center gap-2 px-3 py-1.5 bg-primary-500/20 rounded-full hover:bg-primary-500/30 transition-colors"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
                  </span>
                  <Radio size={14} className="text-primary-400" />
                </Link>
              )}

              <div className="relative w-16 h-16 rounded-xl overflow-hidden shadow-lg flex-shrink-0 group">
                <div className="w-full h-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                  {currentTrack.image ? (
                    <img
                      src={currentTrack.image}
                      alt={currentTrack.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-2xl">🎵</span>
                  )}
                </div>
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-base truncate text-white">{currentTrack.name}</div>
                <div className="text-sm text-gray-400 truncate">
                  {isRadioMode
                    ? `${profileSlug?.replace(/-/g, ' ')} • ${mood?.replace(/-/g, ' ')}`
                    : currentTrack.description || 'Fonorex'}
                </div>
              </div>
              <LikeButton trackId={currentTrack._id} size={20} />

              {/* Stop radio button */}
              {isRadioMode && (
                <button
                  onClick={stopRadio}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                  title="Stop radio"
                >
                  <X size={20} />
                </button>
              )}
            </div>

            {/* Controls */}
            <div className="flex flex-col items-center gap-3 flex-1">
              <div className="flex items-center gap-6">
                {!isRadioMode && (
                  <button
                    onClick={previous}
                    disabled={currentTimeValue <= 3 && history.length === 0}
                    className="text-gray-400 hover:text-white transition-all hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100"
                    aria-label="Previous track"
                  >
                    <SkipBack size={22} />
                  </button>
                )}

                <button
                  onClick={isPlaying ? pause : resume}
                  className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 text-white rounded-full flex items-center justify-center hover:scale-110 transition-all shadow-lg hover:shadow-glow"
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" className="ml-0.5" />}
                </button>

                {!isRadioMode && (
                  <button
                    onClick={next}
                    disabled={queue.length === 0}
                    className="text-gray-400 hover:text-white transition-all hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100"
                    aria-label="Next track"
                  >
                    <SkipForward size={22} />
                  </button>
                )}
              </div>

              {/* Progress Bar */}
              <div className="flex items-center gap-3 w-full max-w-lg">
                <span className="text-xs text-gray-400 w-11 text-right font-medium">{formatTime(currentTimeValue)}</span>
                <div
                  className={`flex-1 h-1.5 bg-white/10 rounded-full group relative ${isRadioMode ? 'cursor-default' : 'cursor-pointer'}`}
                  onClick={handleProgressClick}
                >
                  <div
                    className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full relative transition-all"
                    style={{ width: `${progress}%` }}
                  >
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                <span className="text-xs text-gray-400 w-11 font-medium">{formatTime(duration)}</span>
              </div>
            </div>

            {/* Volume & Queue */}
            <div className="flex items-center gap-5 flex-1 justify-end">
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleMute}
                  className="text-gray-400 hover:text-white transition-all hover:scale-110"
                  aria-label={volume === 0 ? 'Unmute' : 'Mute'}
                >
                  {volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </button>
                <div className="w-28 h-1.5 bg-white/10 rounded-full relative group">
                  <div
                    className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full"
                    style={{ width: `${volume * 100}%` }}
                  />
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={volume}
                    onChange={(e) => setVolume(parseFloat(e.target.value))}
                    className="absolute inset-0 w-full opacity-0 cursor-pointer"
                    aria-label="Volume"
                  />
                </div>
              </div>

              {!isRadioMode && (
                <button
                  onClick={() => setShowQueue(!showQueue)}
                  className={`relative text-gray-400 hover:text-white transition-all hover:scale-110 ${
                    showQueue ? 'text-primary-400' : ''
                  }`}
                  aria-label="Toggle queue"
                >
                  <ListMusic size={22} />
                  {queue.length > 0 && (
                    <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-lg">
                      {queue.length}
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Player Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 backdrop-blur-2xl bg-dark-950/95 text-white border-t border-white/10 z-50 shadow-2xl">
        <div className="px-3 py-3">
          {/* Track Info & Main Controls */}
          <div className="flex items-center gap-3 mb-2">
            {/* Radio indicator for mobile */}
            {isRadioMode && (
              <Link
                to={`/radio/${profileSlug}/${mood}`}
                className="flex items-center justify-center w-6 h-6 bg-primary-500/20 rounded-full flex-shrink-0"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
                </span>
              </Link>
            )}

            {/* Track Image */}
            <div className="relative w-12 h-12 rounded-lg overflow-hidden shadow-lg flex-shrink-0">
              <div className="w-full h-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                {currentTrack.image ? (
                  <img
                    src={currentTrack.image}
                    alt={currentTrack.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-xl">🎵</span>
                )}
              </div>
            </div>

            {/* Track Info */}
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm truncate text-white">{currentTrack.name}</div>
              <div className="text-xs text-gray-400 truncate">
                {isRadioMode
                  ? `${profileSlug?.replace(/-/g, ' ')} • ${mood?.replace(/-/g, ' ')}`
                  : currentTrack.description || 'Fonorex'}
              </div>
            </div>

            {/* Like Button */}
            <LikeButton trackId={currentTrack._id} size={18} className="flex-shrink-0" />

            {/* Play/Pause Button */}
            <button
              onClick={isPlaying ? pause : resume}
              className="w-11 h-11 bg-gradient-to-br from-primary-500 to-secondary-500 text-white rounded-full flex items-center justify-center shadow-lg flex-shrink-0"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" className="ml-0.5" />}
            </button>

            {/* Next Button (only for tracks) or Stop (for radio) */}
            {isRadioMode ? (
              <button
                onClick={stopRadio}
                className="text-gray-400 hover:text-white flex-shrink-0"
                aria-label="Stop radio"
              >
                <X size={20} />
              </button>
            ) : (
              <button
                onClick={next}
                disabled={queue.length === 0}
                className="text-gray-400 disabled:opacity-30 flex-shrink-0"
                aria-label="Next track"
              >
                <SkipForward size={20} />
              </button>
            )}
          </div>

          {/* Progress Bar */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-400 w-9 text-right font-medium">{formatTime(currentTimeValue)}</span>
            <div
              className={`flex-1 h-1 bg-white/10 rounded-full relative ${isRadioMode ? 'cursor-default' : 'cursor-pointer'}`}
              onClick={handleProgressClick}
            >
              <div
                className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-[10px] text-gray-400 w-9 font-medium">{formatTime(duration)}</span>
          </div>
        </div>
      </div>

      {/* Queue Panel */}
      {showQueue && (
        <div className="fixed bottom-28 right-6 w-96 max-h-[500px] backdrop-blur-2xl bg-dark-950/95 border border-white/20 rounded-2xl shadow-2xl overflow-hidden z-50 animate-slide-up">
          <div className="p-5 border-b border-white/10 bg-gradient-to-r from-primary-500/10 to-secondary-500/10">
            <h3 className="font-bold text-white text-lg">Play Queue</h3>
            <p className="text-xs text-gray-400 mt-1">{queue.length} track{queue.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="overflow-y-auto max-h-[400px] scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
            {queue.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-20 h-20 mx-auto mb-4 bg-white/5 rounded-full flex items-center justify-center">
                  <ListMusic size={40} className="text-gray-600" />
                </div>
                <p className="text-gray-400 font-medium">No tracks in queue</p>
                <p className="text-gray-600 text-sm mt-1">Add songs to start listening</p>
              </div>
            ) : (
              queue.map((track, index) => (
                <div
                  key={`${track._id}-${index}`}
                  className="group flex items-center gap-3 p-3 hover:bg-white/5 transition-all border-b border-white/5 last:border-0"
                >
                  <span className="text-xs text-gray-500 w-8 text-right font-medium">{index + 1}</span>
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden shadow-md">
                    {track.image ? (
                      <img src={track.image} alt={track.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xl">🎵</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-medium truncate group-hover:text-primary-400 transition-colors">{track.name}</div>
                    <div className="text-xs text-gray-400 truncate">
                      {formatTime(track.duration)}
                    </div>
                  </div>
                  <button
                    onClick={() => usePlayerStore.getState().removeFromQueue(index)}
                    className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 transition-all text-lg w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-500/10"
                  >
                    ✕
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
}
